import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as z from 'zod';

import { FormGroup } from '../shared/components/FormGroup';
import { Loader } from '../shared/components/Loader';
import { PasswordHints } from '../shared/components/PasswordHints';
import { PasswordInput } from '../shared/components/PasswordInput';
import { usePatientUser } from '../shared/hooks/usePatientUser';

import parentStyles from './RecoverPasswordPage.module.scss';
import styles from './UpdatePasswordForm.module.scss';

const schema = z.object({
  verificationCode: z.string().min(1),
  password: z
    .string()
    .min(1)
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*()_+:])(?=.{8,})/),
});

type FormValues = z.infer<typeof schema>;

export function UpdatePasswordForm() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const username = searchParams.get('username');

  const [error, setError] = useState<string | null>(null);
  const { updatePassword } = usePatientUser(username);
  const [isProcessing, setIsProcessing] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = useCallback(
    async ({ password, verificationCode }: FormValues) => {
      setIsProcessing(true);
      try {
        await updatePassword(password, verificationCode);

        setIsProcessing(false);
        navigate('../success');
      } catch (error) {
        //FIXME: show a toast message with the error
        setError('Unable to update password. Please check the verification code and try again.');
        setIsProcessing(false);
        captureException(error);
      }
    },
    [navigate, updatePassword]
  );

  return (
    <div className={styles.container}>
      <h1 className={parentStyles.title}>
        <FormattedMessage
          id="recover-password-page.create-password-title"
          defaultMessage="Create your new password"
        />
      </h1>
      {error && <div className={styles.error}>{error}</div>}
      <form className={styles.form} noValidate onSubmit={handleSubmit(onSubmit)}>
        <FormGroup error={errors['verificationCode']}>
          <label htmlFor="verificationCode">
            <FormattedMessage
              id="recover-password-page.verification-code-input-label"
              defaultMessage="Enter the verification code we sent to your email"
            />
          </label>
          <input id="verificationCode" type="text" {...register('verificationCode')} />
        </FormGroup>

        <FormGroup
          error={errors['password']}
          errorMessages={{
            too_small: (
              <FormattedMessage
                id="create-password-page.password-input-required"
                defaultMessage="Please create a password"
              />
            ),
            invalid_format: (
              <>
                <strong>
                  <FormattedMessage
                    id="create-password-page.password-input-error"
                    defaultMessage="Please make sure your password contains:"
                  />
                </strong>
                <PasswordHints />
              </>
            ),
          }}
          help={
            <>
              <FormattedMessage
                id="create-password-page.password-input-help"
                defaultMessage="Password must contain:"
              />
              <PasswordHints />
            </>
          }
        >
          <label htmlFor="userPassword">
            <FormattedMessage
              id="create-password-page.password-input-label"
              defaultMessage="Create password"
            />
          </label>
          <PasswordInput id="userPassword" {...register('password')} />
        </FormGroup>

        <div className={styles.actions}>
          <button type="submit" disabled={isProcessing}>
            <FormattedMessage
              id="recover-password-page.form-submit-btn"
              defaultMessage="Update password"
            />
          </button>
        </div>
      </form>
      {isProcessing && <Loader fullscreen />}
    </div>
  );
}
